import axios from 'axios';

export default {
  namespaced: true,

  state: {
    categories: [],
    pieces: {},
    selectedPiece: null,
    searchResults: [],
    containers: [],
    containerVisualization: {
      container_type: '',
      container_number: 0,
      pieceTable: [],
    },
  },

  mutations: {
    SET_CATEGORIES(state, categories) {
      state.categories = categories;
    },
    SET_PIECES(state, { type, pieces }) {
      state.pieces = {
        ...state.pieces,
        [type]: pieces,
      };
    },
    SET_SELECTED_PIECE(state, piece) {
      state.selectedPiece = piece;
    },
    SET_SEARCH_RESULTS(state, results) {
      state.searchResults = results;
    },
    SET_CONTAINERS(state, containers) {
      state.containers = containers;
    },
    UPDATE_PIECE_SORT(state, { type, subtype, pieces }) {
      if (state.pieces[type]) {
        state.pieces[type][subtype] = pieces;
      }
    },
    UPDATE_PIECE_DETAILS(state, { pieceId, details }) {
      // Update piece details in all relevant state locations
      if (state.selectedPiece && state.selectedPiece.id === pieceId) {
        state.selectedPiece = { ...state.selectedPiece, ...details };
      }

      Object.keys(state.pieces).forEach((type) => {
        Object.keys(state.pieces[type] || {}).forEach((subtype) => {
          const pieceIndex = state.pieces[type][subtype].findIndex((p) => p.id === pieceId);
          if (pieceIndex !== -1) {
            state.pieces[type][subtype][pieceIndex] = {
              ...state.pieces[type][subtype][pieceIndex],
              ...details,
            };
          }
        });
      });
    },
    SET_CONTAINER_VISUALIZATION(state, { type, number }) {
      state.containerVisualization.container_type = type;
      state.containerVisualization.container_number = number;
    },
    SET_PIECE_TABLE(state, table) {
      state.containerVisualization.pieceTable = table;
    },
  },

  actions: {
    async fetchCategories({ commit }) {
      const response = await axios.get('/lego/pieces/categories');
      commit('SET_CATEGORIES', response.data.data);
    },

    async fetchPiecesByCategory({ commit }, type) {
      const response = await axios.get(`/lego/pieces/category/${type}`);
      commit('SET_PIECES', { type, pieces: response.data.data });
    },

    async fetchPieceDetails({ commit }, id) {
      const response = await axios.get(`/lego/pieces/${id}`);
      commit('SET_SELECTED_PIECE', response.data.data);
    },

    async searchPieces({ commit }, query) {
      const response = await axios.get(`/lego/pieces/search?q=${query}`);
      commit('SET_SEARCH_RESULTS', response.data.data);
    },

    async fetchContainers({ commit }) {
      const response = await axios.get('/lego/container');
      commit('SET_CONTAINERS', response.data);
    },

    async updatePieceSort({ commit }, { type, subtype, pieces }) {
      const sortData = pieces.reduce((acc, piece, index) => {
        acc[piece.id] = index;
        return acc;
      }, {});

      await axios.post('/lego/pieces/sort', sortData);
      commit('UPDATE_PIECE_SORT', { type, subtype, pieces });
    },

    async updatePieceDetails({ commit }, { id, details }) {
      await axios.post(`/lego/pieces/${id}/details`, details);
      commit('UPDATE_PIECE_DETAILS', { pieceId: id, details });
    },

    async updateTypeName({ dispatch }, { oldType, newType }) {
      await axios.put(`/lego/pieces/type/${oldType}/to/${newType}`);
      await dispatch('fetchCategories');
    },

    async updateSubtypeName({ dispatch }, { type, oldSubtype, newSubtype }) {
      await axios.put(`/lego/pieces/type/${type}/subtype/${oldSubtype}/to/${newSubtype}`);
      await dispatch('fetchPiecesByCategory', type);
    },

    setContainerVisualization({ commit, state }, { type, number }) {
      commit('SET_CONTAINER_VISUALIZATION', { type, number });

      // Calculate piece table
      const table = Array.from({ length: 7 }, () => Array.from({ length: 5 }, () => []));

      Object.values(state.pieces).forEach((pieceType) => {
        Object.values(pieceType).forEach((pieceSubtype) => {
          pieceSubtype.forEach((piece) => {
            if (
              piece.container_type === type
              && parseInt(piece.container_number, 10) === parseInt(number, 10)
            ) {
              if (piece.position_x <= 0 || piece.position_y <= 0) {
                table[0][0].push(piece);
              } else {
                table[piece.position_y - 1][piece.position_x - 1].push(piece);
              }
            }
          });
        });
      });

      commit('SET_PIECE_TABLE', table);
    },
  },

  getters: {
    getCategoryByType:
    (state) => (type) => state.categories.find((category) => category.type === type),
    getPiecesByType: (state) => (type) => state.pieces[type] || {},
    getPieceById: (state) => (id) => {
      let foundPiece = null;
      Object.values(state.pieces).forEach((pieceType) => {
        Object.values(pieceType).forEach((pieceSubtype) => {
          const piece = pieceSubtype.find((p) => p.id === id);
          if (piece) foundPiece = piece;
        });
      });
      return foundPiece;
    },
    containerVisualizationTable: (state) => state.containerVisualization.pieceTable,
  },
};
